<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, shallowRef } from 'vue';
  import Player from 'xgplayer';
  import FlvPlugin from 'xgplayer-flv';
  import HlsPlugin from 'xgplayer-hls';
  import { videoType } from './live-player';
  import { customLang, onLineIgnores, recordIgnores } from './preset';

  /**
   * 西瓜播放器
   */
  defineOptions({
    name: 'XgPlayer',
  });

  const props = defineProps<{
    url: string;
    onLine?: boolean;
  }>();

  const playerRef = shallowRef<HTMLElement>();
  const playerInstance = ref<Player | null>(null);
  const videoUrl = computed(() => props.url);

  onMounted(() => {
    // @ts-ignore
    Player.I18N.extend([customLang]);
    play();
  });

  defineExpose({ play, destroy });

  function play() {
    destroy();
    nextTick(() => {
      console.log(videoUrl.value);
      // 视频地址为空，不播放
      if (!videoUrl.value) {
        return;
      }
      createXgPlayer();
    });
  }

  function destroy() {
    if (playerInstance.value) {
      playerInstance.value.destroy();
      playerInstance.value = null;
    }
  }

  /**
   * 检查插件支持情况
   */
  function checkPluginSupport() {
    const plugins: any[] = [];
    const type = videoType(props.url);

    try {
      if (type === 'flv' && FlvPlugin.isSupported()) {
        plugins.push(FlvPlugin);
      } else if (type === 'hls') {
        const videoElement = document.createElement('video');
        const isNativeHlsSupported = videoElement.canPlayType('application/vnd.apple.mpegurl');

        if (!isNativeHlsSupported && HlsPlugin.isSupported()) {
          plugins.push(HlsPlugin);
        }
      } else if (type !== 'flv' && type !== 'hls') {
        console.warn(`不支持的视频类型: ${type}. 请使用 'flv' 或 'hls'。`);
      }
    } catch (error) {
      console.error('检查插件支持时出错:', error);
    }

    return plugins;
  }

  /**
   * 创建播放器配置
   */
  function createPlayerConfig(plugins: any[]) {
    const ignores = props.onLine ? onLineIgnores : recordIgnores;

    return {
      el: playerRef.value!,
      url: props.url,
      height: '100%',
      width: '100%',
      autoplay: true,
      autoplayMuted: true,
      videoInit: true,
      plugins,
      ignores,
      marginControls: false,
      screenShot: props.onLine,
      lang: 'zh-cn',
      videoAttributes: {
        crossOrigin: 'anonymous',
      },
    };
  }

  /**
   * 创建西瓜播放器实例，播放hls,flv流
   */
  function createXgPlayer() {
    const plugins = checkPluginSupport();
    const config = createPlayerConfig(plugins);

    playerInstance.value = new Player(config);

    // 播放出错之后，点击"刷新"按钮重新播放
    playerInstance.value.getPlugin('error').useHooks('errorRetry', () => {
      playerInstance.value!.setConfig({
        url: props.url,
      });
      // 重置状态
      playerInstance.value!.resetState();
      // 重新播放
      playerInstance.value!.play();
      return false;
    });

    playerInstance.value.on('error', (error: Record<string, any>) => {
      console.error('播放出错:', error);
      // 播放出错时，禁用控制栏插件
      playerInstance.value!.unRegisterPlugin('controls');
    });

    // 视频缓冲足够数据，可以播放
    playerInstance.value.on('canplay', () => {
      console.log('视频缓冲足够数据，可以播放');
    });
  }
</script>

<template>
  <div class="i-player-bg i-player-container">
    <!-- 视频地址为空的提示 -->
    <div v-if="!videoUrl" class="i-player-no__url">
      <div>无播放地址</div>
    </div>
    <div v-else ref="playerRef" class="i-player-container" />
  </div>
</template>
